import {
  Color,
  Vec3,
  Quat,
  Xfo,
  TreeItem,
  GeomItem,
  Lines,
  Plane,
  Disc,
  Cuboid,
  Camera,
  LDRImage,
  Label,
  VideoStreamImage2D,
  VLAAsset,
  SimpleSurfaceMaterial,
  LinesMaterial,
  FlatSurfaceMaterial,
  Cylinder,
} from '@zeainc/zea-engine'
import { adjustAvatarOpacity } from './helpers/adjustAvatarOpacity'

const plane = new Plane(1, 1)
const disc = new Disc(0.5, 64)

const sc = 0.02
const cuboid = new Cuboid(16 * sc, 9 * sc, 3 * sc, true) // 16:9
const pinch = new Vec3(0.1, 0.1, 1)

{
  const positions = cuboid.getVertexAttribute('positions')
  positions.getValue(positions.getValue(0).multiply(pinch))
  positions.getValue(positions.getValue(1).multiply(pinch))
  positions.getValue(positions.getValue(2).multiply(pinch))
  positions.getValue(positions.getValue(3).multiply(pinch))
}

cuboid.computeVertexNormals()

const line = new Lines()
line.setNumVertices(2)
line.setNumSegments(1)
line.setSegmentVertexIndices(0, 0, 1)

{
  const positions = line.getVertexAttribute('positions')
  positions.setValue(0, new Vec3(0.0, 0.0, 0.0))
  positions.setValue(1, new Vec3(0.0, 0.0, 1.0))
}
line.setBoundingBoxDirty()

/**
 * The Avatar is used to display a 3d representation of remote users in a 3d scene.
 */
class Avatar {
  /**
   * Initializes all the components of the Avatar like, user image, labels, transformations, color, etc.
   *
   * @param {object} appData - The appData value. Must contain the renderer
   * @param {object} userData - The userData value.
   * @param {boolean} currentUserAvatar - The currentUserAvatar value.
   * @param {float} avatarScale - The size of the avatar in the view
   * @param {float} scaleAvatarWithFocalDistance - To disable avatar scaling, pass as false.
   */
  constructor(appData, userData, currentUserAvatar = false, avatarScale = 1.0, scaleAvatarWithFocalDistance = false) {
    this.appData = appData
    this.userData = userData
    this.isCurrentUserAvatar = currentUserAvatar
    this.avatarScale = avatarScale
    this.scaleAvatarWithFocalDistance = scaleAvatarWithFocalDistance

    this.treeItem = new TreeItem(this.userData.id)
    this.avatarColor = new Color(userData.color || '#aaaaaa')
    this.pointerColor = new Color(1, 0, 0)

    this.vrUIControllerId = -1
    this.pointerGeomItems = []

    if (!this.isCurrentUserAvatar) {
      this.camera = new Camera()
      this.cameraBound = false

      let avatarImage
      const hasImage = this.userData.picture && this.userData.picture != ''
      if (hasImage) {
        avatarImage = new LDRImage('user' + this.userData.id + 'AvatarImage')
        avatarImage.load(this.userData.picture)
      } else {
        const firstName = this.userData.firstName || this.userData.given_name || ''
        const lastName = this.userData.lastName || this.userData.family_name || ''
        let firstLetter = ''
        let secondLetter = ''
        if (firstName) {
          firstLetter = firstName.charAt(0)
        }
        if (lastName) {
          secondLetter = lastName.charAt(0)
        } else if (firstLetter) {
          // if no last name but it does have a firstName,
          // use the firstName's second letter
          secondLetter = firstName.charAt(1)
        }
        avatarImage = new Label('Name')
        const isLightColor = this.avatarColor.luminance() > 0.4
        if (isLightColor) {
          avatarImage.fontColorParam.value = new Color(0, 0, 0)
        } else {
          avatarImage.fontColorParam.value = new Color(1, 1, 1)
        }
        avatarImage.backgroundColorParam.value = this.avatarColor
        avatarImage.fontSizeParam.value = 42
        avatarImage.borderRadiusParam.value = 0
        avatarImage.borderWidthParam.value = 0
        avatarImage.marginParam.value = 12
        avatarImage.strokeBackgroundOutlineParam.value = false
        avatarImage.textParam.value = String(firstLetter + secondLetter).toUpperCase()
      }

      const avatarImageMaterial = new FlatSurfaceMaterial('user' + this.userData.id + 'AvatarImageMaterial')
      avatarImageMaterial.baseColorParam.value = this.avatarColor
      avatarImageMaterial.baseColorParam.setImage(avatarImage)

      avatarImageMaterial.visibleInGeomDataBuffer = false
      this.avatarImageGeomItem = new GeomItem('avatarImage', disc, avatarImageMaterial)

      this.avatarImageXfo = new Xfo()
      this.avatarImageXfo.sc.set(0.2, 0.2, 0.2)
      this.avatarImageXfo.ori.setFromAxisAndAngle(new Vec3(0, 1, 0), Math.PI)
      this.avatarImageGeomItem.localXfoParam.value = this.avatarImageXfo

      {
        ///////////////////////////////////////////////
        // Nameplate on the back of the avatar.

        const firstName = this.userData.firstName || this.userData.given_name || ''
        const lastName = this.userData.lastName || this.userData.family_name || ''
        const avatarNameplate = new Label('Name')
        const isLightColor = this.avatarColor.luminance() > 0.4
        if (isLightColor) {
          avatarNameplate.fontColorParam.value = new Color(0, 0, 0)
        } else {
          avatarNameplate.fontColorParam.value = new Color(1, 1, 1)
        }
        avatarNameplate.backgroundColorParam.value = this.avatarColor
        avatarNameplate.fontSizeParam.value = 42
        avatarNameplate.borderRadiusParam.value = 0
        avatarNameplate.borderWidthParam.value = 0
        avatarNameplate.marginParam.value = 12
        avatarNameplate.strokeBackgroundOutlineParam.value = false
        avatarNameplate.textParam.value = `${firstName} ${lastName}`
        avatarNameplate.on('labelRendered', (event) => {
          const avatarNameplateXfo = this.avatarNameplateGeomItem.localXfoParam.value
          const height = event.height / event.width
          avatarNameplateXfo.sc.set(-1.5, height * 1.5, 1)
          this.avatarNameplateGeomItem.localXfoParam.value = avatarNameplateXfo
        })

        const avatarNameplateMaterial = new FlatSurfaceMaterial('user' + this.userData.id + 'AvatarImageMaterial')
        avatarNameplateMaterial.baseColorParam.setImage(avatarNameplate)
        avatarNameplateMaterial.visibleInGeomDataBuffer = false
        this.avatarNameplateGeomItem = new GeomItem('avatarNameplate', plane, avatarNameplateMaterial)

        this.avatarImageGeomItem.addChild(this.avatarNameplateGeomItem, false)
      }

      ///////////////////////////////////////////////

      const avatarImageBorderMaterial = new FlatSurfaceMaterial('avatarImageBorderMaterial')
      avatarImageBorderMaterial.baseColorParam.value = hasImage ? this.avatarColor : new Color(0, 0, 0, 1)
      avatarImageBorderMaterial.visibleInGeomDataBuffer = false
      const avatarImageBorderGeomItem = new GeomItem('avatarImageBorder', disc, avatarImageBorderMaterial)

      this.avatarImageGeomItem.addChild(avatarImageBorderGeomItem, false)
      const borderXfo = new Xfo()
      borderXfo.sc.set(1.1, 1.1, 1.1)
      borderXfo.tr.set(0.0, 0.0, -0.005)
      avatarImageBorderGeomItem.localXfoParam.value = borderXfo
    }
  }

  /**
   * Usually called on `USER_VIDEO_STARTED` Session action this attaches the video MediaStream to the avatar cam geometry item.
   *
   * @param {MediaStream} video - The video param.
   */
  attachRTCStream(video) {
    if (!this.avatarCamGeomItem) {
      const videoItem = new VideoStreamImage2D('webcamStream')
      videoItem.setVideoStream(video)

      const avatarCamMaterial = new FlatSurfaceMaterial('user' + this.userData.id + 'AvatarImageMaterial')
      avatarCamMaterial.baseColorParam.value = this.avatarColor
      avatarCamMaterial.baseColorParam.setImage(videoItem)
      avatarCamMaterial.visibleInGeomDataBuffer = false
      this.avatarCamGeomItem = new GeomItem('avatarImage', plane, avatarCamMaterial)

      const sc = 0.02
      const avatarCamXfo = new Xfo()
      avatarCamXfo.sc.set(16 * sc, 9 * sc, 1)
      avatarCamXfo.tr.set(0, 0, -0.1 * sc)
      this.avatarCamGeomItem.localXfoParam.value = avatarCamXfo

      const aspect = video.videoWidth / video.videoHeight
      avatarCamXfo.sc.x = avatarCamXfo.sc.y * aspect
      this.avatarImageGeomItem.localXfoParam.value = avatarCamXfo
    }

    if (this.currentViewMode == 'CameraAndPointer') {
      this.treeItem.getChild(0).removeAllChildren()
      this.treeItem.getChild(0).addChild(this.avatarCamGeomItem, false)
    }
  }

  /**
   * As opposite of the `attachRTCStream` method, this is usually called on `USER_VIDEO_STOPPED` Session action, removing the RTC Stream from the treeItem
   */
  detachRTCStream() {
    if (this.currentViewMode == 'CameraAndPointer') {
      this.treeItem.getChild(0).removeAllChildren()

      const sc = 0.02
      this.avatarImageXfo.sc.set(9 * sc, 9 * sc, 9 * sc)
      this.avatarImageXfo.tr.set(0, 0, -0.1 * sc)
      this.avatarImageGeomItem.localXfoParam.value = this.avatarImageXfo
      this.treeItem.getChild(0).addChild(this.avatarImageGeomItem, false)
    }
  }

  /**
   * Returns Avatar's Camera tree item.
   *
   * @return {Camera} The return value.
   */
  getCamera() {
    return this.camera
  }

  /**
   * Traverses Camera's sibling items and hide them, but shows Camera item.
   */
  bindCamera() {
    this.cameraBound = true

    const cameraOwner = this.camera.getOwner()
    if (cameraOwner) {
      cameraOwner.traverse((subTreeItem) => {
        if (subTreeItem != this.camera) subTreeItem.setVisible(false)
      })
    }
  }

  /**
   * Traverses Camera's sibling items and show them, but hides Camera item.
   */
  unbindCamera() {
    this.cameraBound = false

    const cameraOwner = this.camera.getOwner()
    if (cameraOwner) {
      cameraOwner.traverse((subTreeItem) => {
        if (subTreeItem != this.camera) subTreeItem.setVisible(true)
      })
    }
  }

  /**
   * The setCameraAndPointerRepresentation method.
   * @private
   */
  setCameraAndPointerRepresentation() {
    this.treeItem.removeAllChildren()
    this.treeItem.opacityParam.value = 1
    this.currentViewMode = 'CameraAndPointer'

    if (this.isCurrentUserAvatar) return

    const material = new SimpleSurfaceMaterial('user' + this.userData.id + 'Material')
    material.visibleInGeomDataBuffer = false
    material.baseColorParam.value = new Color(0.2, 0.2, 0.2, 1.0)
    const geomItem = new GeomItem('camera', cuboid, material)
    const geomXfo = new Xfo()
    geomItem.geomOffsetXfoParam.value = geomXfo

    this.pointerXfo = new Xfo()
    this.pointerXfo.sc.set(1, 1, 0)

    this.pointerMaterial = new LinesMaterial('pointermat')
    this.pointerMaterial.baseColorParam.value = this.avatarColor

    this.pointerItem = new GeomItem('Pointer', line, this.pointerMaterial)
    this.pointerItem.localXfoParam.value = this.pointerXfo
    this.pointerItem.disableBoundingBox = true

    // If the webcam stream is available, attach it
    // else attach the avatar image. (which should always be available)
    if (this.avatarCamGeomItem) {
      geomItem.addChild(this.avatarCamGeomItem, false)
    } else if (this.avatarImageGeomItem) {
      this.avatarImageXfo.sc.set(9 * sc, 9 * sc, 9 * sc)
      this.avatarImageXfo.tr.set(0, 0, -0.1 * sc)
      this.avatarImageGeomItem.localXfoParam.value = this.avatarImageXfo
      geomItem.addChild(this.avatarImageGeomItem, false)
    }

    if (this.avatarNameplateGeomItem) {
      // Move the nameplate to the back of the avatar.
      const avatarNameplateXfo = new Xfo()
      // Calculate the offset needed to move the nameplate to the back of the camera geom.
      const zOffset = (cuboid.sizeZParam.value / this.avatarNameplateGeomItem.globalXfoParam.value.sc.z) * -1.1
      avatarNameplateXfo.tr.set(0, 0, zOffset)
      this.avatarNameplateGeomItem.localXfoParam.value = avatarNameplateXfo
    }

    if (this.audioStreamItem) {
      geomItem.addChild(this.audioStreamItem, false)
    }

    this.treeItem.addChild(geomItem, false)
    this.treeItem.addChild(this.pointerItem, false)

    this.treeItem.addChild(this.camera, false)
    if (this.cameraBound) {
      geomItem.setVisible(false)
    }
  }

  /**
   * The updateCameraAndPointerPose method.
   * @param {object} data - The data param.
   * @private
   */
  updateCameraAndPointerPose(data, camera) {
    if (this.isCurrentUserAvatar) return

    if (data.viewXfo) {
      adjustAvatarOpacity(this, data.viewXfo, camera)

      if (this.scaleAvatarWithFocalDistance && data.focalDistance) {
        // When closer than 5 meters, the avatar scales to avoid getting too small.
        const sc = (data.focalDistance / 5) * this.avatarScale
        data.viewXfo.sc.set(sc, sc, sc)
      }
      this.treeItem.getChild(0).localXfoParam.value = data.viewXfo
      this.pointerXfo.sc.z = 0
      this.treeItem.getChild(1).localXfoParam.value = this.pointerXfo

      this.viewXfo = data.viewXfo
      if (data.focalDistance) {
        this.focalDistance = data.focalDistance
      }
    } else if (data.movePointer) {
      this.pointerXfo.tr = data.movePointer.start
      this.pointerXfo.ori.setFromDirectionAndUpvector(data.movePointer.dir, new Vec3(0, 0, 1))
      this.pointerXfo.sc.z = data.movePointer.length
      this.pointerItem.localXfoParam.value = this.pointerXfo
      this.pointerItem.visibleParam.value = true
      this.pointerMaterial.baseColorParam.value = this.avatarColor
    } else if (data.highlightPointer) {
      this.pointerItem.visibleParam.value = true
      this.pointerMaterial.baseColorParam.value = this.pointerColor
    } else if (data.unhighlightPointer) {
      this.pointerItem.visibleParam.value = true
      this.pointerMaterial.baseColorParam.value = this.avatarColor
    } else if (data.hidePointer) {
      this.pointerXfo.sc.z = 0
      this.pointerItem.visibleParam.value = false
    }
  }

  /**
   * The setVRRepresentation method.
   * @param {object} data - The data param.
   * @private
   */
  setVRRepresentation(data) {
    this.treeItem.removeAllChildren()
    this.treeItem.opacityParam.value = 1
    this.currentViewMode = 'VR'

    const hmdHolder = new TreeItem('hmdHolder')
    if (this.audioStreamItem) {
      hmdHolder.addChild(this.audioStreamItem)
    }

    if (this.avatarImageGeomItem) {
      this.avatarImageXfo.sc.set(0.12, 0.12, 0.12)
      this.avatarImageXfo.tr.set(0, -0.04, -0.135)
      this.avatarImageGeomItem.localXfoParam.value = this.avatarImageXfo
      hmdHolder.addChild(this.avatarImageGeomItem, false)
    }

    if (this.avatarNameplateGeomItem) {
      const avatarNameplateXfo = this.avatarNameplateGeomItem.localXfoParam.value
      avatarNameplateXfo.tr.set(0, 0, -0.4)
      this.avatarNameplateGeomItem.localXfoParam.value = avatarNameplateXfo
    }

    this.treeItem.addChild(hmdHolder)

    if (this.camera) hmdHolder.addChild(this.camera, false)

    if (this.hmdGeomItem) {
      if (!this.isCurrentUserAvatar) hmdHolder.addChild(this.hmdGeomItem, false)
      if (this.cameraBound) {
        this.hmdGeomItem.setVisible(false)
      }
    } else {
      const resourceLoader = this.appData.scene.getResourceLoader()

      let hmdAssetId
      switch (data.hmd) {
        case 'Vive':
          hmdAssetId = 'ZeaEngine/Vive.vla'
          break
        case 'Oculus':
          hmdAssetId = 'ZeaEngine/Oculus.vla'
          break
        default:
          hmdAssetId = 'ZeaEngine/Vive.vla'
          break
      }

      if (!this.vrAsset) {
        if (!resourceLoader.commonResources[hmdAssetId]) {
          // Cache the asset so if an avatar needs to display,
          // it can use the same asset.
          const asset = new VLAAsset(hmdAssetId)
          asset.load(resourceLoader.systemUrls[hmdAssetId])
          resourceLoader.commonResources[hmdAssetId] = asset
        }

        this.vrAsset = resourceLoader.getCommonResource(hmdAssetId)
        const bindHMD = () => {
          const materialLibrary = this.vrAsset.getMaterialLibrary()
          const materialNames = materialLibrary.getMaterialNames()
          for (const name of materialNames) {
            const material = materialLibrary.getMaterial(name, false)
            if (material) {
              material.visibleInGeomDataBuffer = false
              material.setShaderName('SimpleSurfaceShader')
            }
          }

          if (!this.isCurrentUserAvatar) {
            const hmdGeomItem = this.vrAsset.getChildByName('HMD').clone({ assetItem: this.vrAsset })
            hmdGeomItem.visibleParam.value = true
            const xfo = hmdGeomItem.localXfoParam.value
            xfo.tr.set(0, -0.03, -0.03)
            xfo.ori.setFromAxisAndAngle(new Vec3(0, 1, 0), Math.PI)
            xfo.sc.set(0.001) // VRAsset units are in mm. convert meters
            hmdGeomItem.localXfoParam.value = xfo

            this.hmdGeomItem = hmdGeomItem

            if (this.cameraBound) {
              this.hmdGeomItem.setVisible(false)
            }
            hmdHolder.addChild(this.hmdGeomItem, false)
          }
        }

        if (this.vrAsset.isLoaded()) bindHMD()
        else {
          this.vrAsset.on('loaded', bindHMD)
        }
      }
    }

    this.controllerTrees = []
  }

  /**
   * The updateVRPose method.
   * @param {object} data - The data param.
   * @private
   */
  updateVRPose(data, camera) {
    const setupController = (i) => {
      console.log('setupController:', i)
      if (this.controllerTrees[i]) {
        this.treeItem.addChild(this.controllerTrees[i], false)
      } else {
        const treeItem = new TreeItem('handleHolder' + i)
        this.controllerTrees[i] = treeItem
        this.treeItem.addChild(this.controllerTrees[i], false)

        const tipItem = new TreeItem('Tip')
        // Note: the tip of the controller need to be off
        // the end of the controller. getGeomItemAtTip
        // now searches a grid in that area and so we need to
        // ensure that the grid does not touch the controller,
        // else it will return the controller geom from
        // the getGeomItemAtTip function
        const tipXfo = new Xfo()
        tipXfo.tr.set(0.0, -0.05, -0.13)
        // tilt the pointer down a little to be easier on the wrists.
        tipXfo.ori.setFromAxisAndAngle(new Vec3(1, 0, 0), Math.PI * 0.8)
        tipItem.localXfoParam.value = tipXfo
        treeItem.addChild(tipItem, false)

        const setupControllerGeom = () => {
          let srcControllerTree
          let scale = 1.0
          const controllerData = data.controllers[i]
          if (data.hmd == 'Vive') {
            srcControllerTree = this.vrAsset.getChildByName('Controller')
          } else {
            switch (controllerData.handedness) {
              case 'left':
                srcControllerTree = this.vrAsset.getChildByName('LeftController')
                scale = 0.85 // Scale down the Occulus controllers slightly
                break
              case 'right':
                srcControllerTree = this.vrAsset.getChildByName('RightController')
                scale = 0.85 // Scale down the Occulus controllers slightly
                break
              case 'none':
              case 'left-right':
              case 'left-right-none':
                srcControllerTree = this.vrAsset.getChildByName('Controller')
                break
              default:
                break
            }
          }
          if (srcControllerTree) {
            const controllerTree = srcControllerTree.clone({ assetItem: this.vrAsset })
            const quat = new Quat()
            quat.setFromAxisAndAngle(new Vec3(0, 1, 0), Math.PI)
            const xfo = new Xfo(
              new Vec3(0, -0.035, -0.085),
              quat,
              new Vec3(0.001 * scale, 0.001 * scale, 0.001 * scale) // VRAsset units are in mm. convert meters
            )
            controllerTree.localXfoParam.value = xfo
            treeItem.addChild(controllerTree, false)
          } else {
            console.warn('Unable to display controller for :', controllerData)
          }
        }
        if (this.vrAsset.isLoaded()) setupControllerGeom()
        else this.vrAsset.once('loaded', setupControllerGeom)
      }
    }

    if (data.viewXfo) {
      adjustAvatarOpacity(this, data.viewXfo, camera)
      this.treeItem.getChild(0).globalXfoParam.value = data.viewXfo
      this.viewXfo = data.viewXfo
    }

    if (data.controllers) {
      for (let i = 0; i < data.controllers.length; i++) {
        if (data.controllers[i] && !this.controllerTrees[i]) {
          setupController(i)
        }
        this.controllerTrees[i].globalXfoParam.value = data.controllers[i].xfo
      }
    }
    if (data.showUIPanel) {
      const controllerId = data.showUIPanel.controllerId
      if (!this.uiGeomItem) {
        const uimat = new FlatSurfaceMaterial('uimat')
        uimat.baseColorParam.value = new Color(0.4, 0.4, 0.4)

        this.uiGeomItem = new GeomItem('VRControllerUI', plane, uimat)
      }

      this.uiGeomItem.localXfoParam.value.fromJSON(data.showUIPanel.xfo)

      const uiGeomOffsetXfo = new Xfo()
      uiGeomOffsetXfo.sc.set(data.showUIPanel.size.x, data.showUIPanel.size.y, 1)
      // Flip it over so we see the front.
      // uiGeomOffsetXfo.ori.setFromAxisAndAngle(new Vec3(0, 1, 0), Math.PI)
      this.uiGeomItem.geomOffsetXfoParam.value = uiGeomOffsetXfo

      this.vrUIControllerId = controllerId
      const controllerTree = this.controllerTrees[this.vrUIControllerId]
      controllerTree.addChild(this.uiGeomItem, false)
    } else if (data.updateUIPanel) {
      if (this.uiGeomItem) {
        this.uiGeomOffsetXfo.sc.set(data.updateUIPanel.size.x, data.updateUIPanel.size.y, 1)
        this.uiGeomItem.setGeomOffsetXfo(this.uiGeomOffsetXfo)
      }
    } else if (data.hideUIPanel) {
      if (this.uiGeomItem && this.vrUIControllerId != -1) {
        const controllerTree = this.controllerTrees[this.vrUIControllerId]
        try {
          controllerTree.removeChildByHandle(this.uiGeomItem)
        } catch (e) {
          console.warn('Error removing UIPanel')
        }
        this.vrUIControllerId = -1
      }
    } else if (data.showPointer) {
      const controllerId = data.showPointer.controllerId

      if (
        !this.pointerGeomItems[controllerId] ||
        (data.showPointer.thickness == 0 && this.pointerGeomItems[controllerId].geomParam.value instanceof Cylinder) ||
        (data.showPointer.thickness > 0 && this.pointerGeomItems[controllerId].geomParam.value instanceof Lines)
      ) {
        let geom
        let material
        if (data.showPointer.thickness > 0) {
          geom = new Cylinder(data.showPointer.thickness, 1.0, 6, 2, true, true)
          material = new FlatSurfaceMaterial('line')
        } else {
          geom = line
          material = new LinesMaterial('line')
        }
        const pointerGeomItem = new GeomItem('PointerRay', geom, material)
        pointerGeomItem.setSelectable(false)
        this.pointerGeomItems[controllerId] = pointerGeomItem
      }

      const pointerGeomItem = this.pointerGeomItems[controllerId]
      const tipItem = this.controllerTrees[controllerId].getChild(0)
      pointerGeomItem.localXfoParam.value.fromJSON(data.showPointer.xfo)
      const material = pointerGeomItem.materialParam.value
      material.baseColorParam.value.fromJSON(data.showPointer.color)

      tipItem.addChild(pointerGeomItem, false)
    } else if (data.updatePointer) {
      const controllerId = data.updatePointer.controllerId
      const pointerGeomItem = this.pointerGeomItems[controllerId]
      if (pointerGeomItem) {
        pointerGeomItem.localXfoParam.value.fromJSON(data.updatePointer.xfo)
      }
    } else if (data.hidePointer) {
      const controllerId = data.hidePointer.controllerId
      const pointerGeomItem = this.pointerGeomItems[controllerId]
      if (pointerGeomItem) {
        const tipItem = this.controllerTrees[controllerId].getChild(0)
        tipItem.removeChildByHandle(pointerGeomItem)
      }
    }
  }

  /**
   * Method that executes the representation methods for the specified `interfaceType` in the data object.
   * <br>
   * Valid `interfaceType` values: `CameraAndPointer`, `Vive` and `VR`
   *
   * @param {object} data - The data param.
   */
  updatePose(data, camera) {
    switch (data.interfaceType) {
      case 'CameraAndPointer':
        if (this.currentViewMode !== 'CameraAndPointer') {
          this.setCameraAndPointerRepresentation()
        }
        this.updateCameraAndPointerPose(data, camera)
        break
      case 'Vive': // Old recordings.
      case 'VR':
        if (this.currentViewMode !== 'VR') {
          this.setVRRepresentation(data)
        }
        this.updateVRPose(data, camera)
        break
    }
  }

  /**
   * Removes Avatar's TreeItem from the renderer.
   */
  destroy() {
    this.appData.renderer.removeTreeItem(this.treeItem)
  }
}

export default Avatar
